import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { CalculatorInputs } from "./types";
import { calculateAllResults } from "./calculations";
import CalculatorResults from "./CalculatorResults";
import ScrollReveal from "@/components/ScrollReveal";

const SavingsCalculator = () => {
  const { t } = useTranslation();
  
  const [inputs, setInputs] = useState<CalculatorInputs>({
    employees: 4,
    nights: 14,
    hotelRate: 1600,
    guestlyRate: 1300,
    extraFees: 0,
    includeGymValue: true,
    currency: "SEK",
  });

  const results = calculateAllResults(inputs);

  const updateInput = (key: keyof CalculatorInputs, value: number | boolean) => {
    setInputs((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <section className="w-full py-16 bg-background">
      <div className="container mx-auto px-6">
        <div className="max-w-4xl mx-auto space-y-8">
          <ScrollReveal>
            <div className="bg-card rounded-lg border border-border p-8 shadow-sm space-y-8">
              <h2 className="text-2xl font-bold text-foreground">
                {t("corporateCalculator.calculator.title", {
                  defaultValue: "Calculate Your Savings",
                })}
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Number of Employees */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="employees" className="text-base">
                      {t("corporateCalculator.calculator.inputs.employees")}
                    </Label>
                    <Input
                      id="employees"
                      type="number"
                      min="1"
                      max="50"
                      value={inputs.employees}
                      onChange={(e) =>
                        updateInput("employees", parseInt(e.target.value) || 1)
                      }
                      className="w-20 text-right"
                    />
                  </div>
                  <Slider
                    min={1}
                    max={50}
                    step={1}
                    value={[inputs.employees]}
                    onValueChange={([value]) => updateInput("employees", value)}
                    className="w-full"
                  />
                </div>

                {/* Nights per Employee */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="nights" className="text-base">
                      {t("corporateCalculator.calculator.inputs.nights")}
                    </Label>
                    <Input
                      id="nights"
                      type="number"
                      min="1"
                      max="365"
                      value={inputs.nights}
                      onChange={(e) =>
                        updateInput("nights", parseInt(e.target.value) || 1)
                      }
                      className="w-20 text-right"
                    />
                  </div>
                  <Slider
                    min={1}
                    max={365}
                    step={1}
                    value={[inputs.nights]}
                    onValueChange={([value]) => updateInput("nights", value)}
                    className="w-full"
                  />
                </div>

                {/* Hotel Rate */}
                <div className="space-y-2">
                  <Label htmlFor="hotelRate" className="text-base">
                    {t("corporateCalculator.calculator.inputs.hotelRate")}
                  </Label>
                  <Input
                    id="hotelRate"
                    type="number"
                    min="0"
                    step="100"
                    value={inputs.hotelRate}
                    onChange={(e) =>
                      updateInput("hotelRate", parseInt(e.target.value) || 0)
                    }
                    className="w-full"
                  />
                  <p className="text-xs text-muted-foreground">
                    {t("corporateCalculator.calculator.helpers.hotelRate")}
                  </p>
                </div>

                {/* Guestly Rate */}
                <div className="space-y-2">
                  <Label htmlFor="guestlyRate" className="text-base">
                    {t("corporateCalculator.calculator.inputs.guestlyRate")}
                  </Label>
                  <Input
                    id="guestlyRate"
                    type="number"
                    min="0"
                    step="100"
                    value={inputs.guestlyRate}
                    onChange={(e) =>
                      updateInput("guestlyRate", parseInt(e.target.value) || 0)
                    }
                    className="w-full"
                  />
                  <p className="text-xs text-muted-foreground">
                    {t("corporateCalculator.calculator.helpers.guestlyRate")}
                  </p>
                </div>

                {/* Extra Fees */}
                <div className="space-y-2">
                  <Label htmlFor="extraFees" className="text-base">
                    {t("corporateCalculator.calculator.inputs.extraFees")}
                  </Label>
                  <Input
                    id="extraFees"
                    type="number"
                    min="0"
                    step="50"
                    value={inputs.extraFees}
                    onChange={(e) =>
                      updateInput("extraFees", parseInt(e.target.value) || 0)
                    }
                    className="w-full"
                  />
                </div>

                {/* Include Gym Value */}
                <div className="flex items-center justify-between space-x-4">
                  <Label htmlFor="includeGym" className="text-base">
                    {t("corporateCalculator.calculator.inputs.includeGym")}
                  </Label>
                  <Switch
                    id="includeGym"
                    checked={inputs.includeGymValue}
                    onCheckedChange={(checked) =>
                      updateInput("includeGymValue", checked)
                    }
                  />
                </div>
              </div>
            </div>
          </ScrollReveal>

          {/* Results */}
          <CalculatorResults results={results} />
        </div>
      </div>
    </section>
  );
};

export default SavingsCalculator;
